import { parseBlob } from 'music-metadata';

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Loads public/music/playlists.json and builds playlists straight from its
// embedded title/artist/album/duration — no per-track network fetch, so the
// track list appears immediately instead of waiting on every mp3 to be
// downloaded and parsed for ID3 tags. Album art still has to come from the
// actual file (see loadTrackArtwork below); embedding it here too would
// bloat the manifest a lot for not much gain.
//
// Tracks are served straight from public/music by URL. (They used to be
// pulled through require.context(), which made webpack copy every mp3
// into static/media on top of CRA's verbatim copy of public/ — so each
// deploy shipped the whole library twice.)
export async function loadPlaylists() {
  const manifest = await fetch(`${process.env.PUBLIC_URL}/music/playlists.json`).then((res) => res.json());

  return manifest.playlists.map((playlist) => {
    const tracks = playlist.tracks
      .map((entry) => {
        const src = `${process.env.PUBLIC_URL}/music/${encodeURIComponent(entry.file)}`;
        return {
          id: entry.file.replace(/\.mp3$/, ''),
          filename: entry.file,
          src,
          title: entry.title || entry.file,
          artist: entry.artist || '',
          album: entry.album || '',
          duration: entry.duration || '',
        };
      });
    return { id: playlist.id, name: playlist.name, tracks };
  });
}

// Lazily fetches one track's embedded cover art. Called on demand (for the
// currently cued/playing track) rather than for the whole library up
// front, since getting the picture means downloading the actual audio
// file. Results are cached (by filename) so re-visiting a track doesn't
// re-fetch it.
const artworkCache = new Map();

export function loadTrackArtwork(filename, src) {
  if (artworkCache.has(filename)) {
    return artworkCache.get(filename);
  }
  const promise = (async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const { common } = await parseBlob(blob);
      if (common.picture && common.picture.length > 0) {
        const pic = common.picture[0];
        const base64 = arrayBufferToBase64(pic.data);
        return `data:${pic.format};base64,${base64}`;
      }
      return null;
    } catch (err) {
      console.warn(`Couldn't load artwork for "${filename}":`, err);
      return null;
    }
  })();
  artworkCache.set(filename, promise);
  return promise;
}
